import {MediaPlayIcon} from '@ui/icons/media/media-play';
import {Button} from '@ui/buttons/button';
import {useNavigate, useParams} from 'react-router-dom';
import {AnimatePresence, m} from 'framer-motion';
import {Skeleton} from '@ui/skeleton/skeleton';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import clsx from 'clsx';
import {useSettings} from '@ui/settings/use-settings';
import React, {Fragment, useMemo, useState} from 'react';
import {EpisodeSelector} from '@app/videos/watch-page/episode-selector';
import {MediaEpisodesIcon} from '@ui/icons/media/media-episodes';
import {Trans} from '@ui/i18n/trans';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {UseDownloadPageResponse} from '@app/download/requests/use-download-page-video';
import {Lien} from '@app/titles/models/lien';
import {Episode} from '@app/titles/models/episode';
import {getDownloadLink} from '@app/download/download-page/get-download-link';
import {apiClient} from '@common/http/query-client';
import {Video} from '@app/titles/models/video';

const className = 'flex items-center flex-wrap gap-14';

interface Props {
  data: UseDownloadPageResponse | undefined;
  updateMedia: (media: Episode | Lien) => void;
  currentVideo?: Lien | Video; // nouvelle propriété pour la vidéo courante
}

/**
 * Fonction asynchrone qui permet de "débrider" la vidéo.
 */
const MediaDebrider = async (src: number) => {
  try {
    const response = await apiClient.post('debriding', {id: src, type: 1});
    if (response.data.url) {
      response.data.id = src;
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error debriding media:', error);
    return null;
  }
};

export function DownloadPageAlternativeVideos({
  data,
  updateMedia,
  currentVideo,
}: Props) {
  const {streaming} = useSettings();
  const title = data?.title;
  const episode = data?.episode;
  const video = data?.video;
  const navigate = useNavigate();

  const showEpisodeSelector =
    title &&
    episode &&
    video &&
    (video.type === 'embed' ||
      video.type === 'external' ||
      video.type === 'stream');
  if (!showEpisodeSelector && !streaming.show_video_selector) {
    return null;
  }

  return (
    <div className="mt-14 flex items-start justify-between gap-48">
      {streaming.show_video_selector && (
        <Fragment>
          <VideoDropdown
            className="lg:hidden"
            videos={data?.alternative_videos}
            updateMedia={updateMedia}
          />
          <div className="max-lg:hidden">
            <AnimatePresence initial={false} mode="wait">
              {data ? (
                <VideoList
                  videos={data.alternative_videos}
                  updateMedia={updateMedia}
                  currentVideo={currentVideo} // Passage de la vidéo actuellement lue
                />
              ) : (
                <Skeletons />
              )}
            </AnimatePresence>
          </div>
        </Fragment>
      )}
      {showEpisodeSelector && (
        <EpisodeSelector
          title={title}
          currentEpisode={episode}
          onSelected={episode => {
            navigate(getDownloadLink(undefined, episode));
            updateMedia(episode);
          }}
          trigger={
            <Button
              variant="outline"
              className="min-h-40"
              startIcon={<MediaEpisodesIcon />}
            >
              <Trans message="Episodes" />
            </Button>
          }
        />
      )}
    </div>
  );
}

interface VideoDropdownProps {
  videos?: Lien[];
  className?: string;
  updateMedia: (media: Lien) => void;
}

function VideoDropdown({videos, className, updateMedia}: VideoDropdownProps) {
  return (
    <MenuTrigger>
      <Button
        variant="outline"
        className={clsx('min-h-40', className)}
        startIcon={<MediaPlayIcon />}
      >
        <Trans message="Other sources" />
      </Button>
      <Menu>
        {videos &&
          videos.map(video => (
            <MenuItem
              value={video.id}
              key={video.id}
              startIcon={<MediaPlayIcon />}
              endSection={<QualityBadge video={video} />}
              onSelected={() => {
                updateMedia(video);
              }}
            >
              {video.title?.name}
            </MenuItem>
          ))}
      </Menu>
    </MenuTrigger>
  );
}

interface QualityBadgeProps {
  video: Lien;
}

function QualityBadge({video}: QualityBadgeProps) {
  if (!video.quality || video.quality === 'default') {
    return null;
  }

  return (
    <div>
      <span className="rounded border bg-[#3b5998] px-6 text-xs font-bold uppercase">
        {video.quality}
      </span>
      <span className="ml-2 rounded border bg-[#1D2D25] px-6 text-xs font-bold uppercase">
        {video.language}
      </span>
    </div>
  );
}

interface VideoListProps {
  videos: Lien[];
  updateMedia: (media: Lien) => void;
}

// Toujours dans download-page-alternative-videos.tsx

interface VideoListProps {
  videos: Lien[];
  updateMedia: (media: Lien) => void;
  currentVideo?: Lien | Video; // Nouvelle propriété pour la vidéo actuellement lue
}

function VideoList({videos, updateMedia, currentVideo}: VideoListProps) {
  const handleDebrid = async (video: Lien) => {
    return await MediaDebrider(video.id);
  };

  if (videos.length < 2) {
    return null;
  }

  return (
    <m.div
      key="alternative-sources"
      className={className}
      {...opacityAnimation}
    >
      {videos.map(video => (
        <Button
          key={video.id}
          variant="outline"
          // La couleur est définie en fonction de la vidéo actuellement lue passée en prop
          color={
            currentVideo && currentVideo.id === video.id ? 'primary' : 'chip'
          }
          startIcon={<MediaPlayIcon aria-hidden />}
          className="min-h-40 gap-10"
          onClick={async () => {
            // Mise à jour immédiate de la vidéo sélectionnée pour modifier la couleur
            updateMedia(video);
            // Appel asynchrone pour débriader la vidéo et mettre à jour sa source

            const data = await handleDebrid(video);
            //   console.log('Debrid data:', data);
            if (data) {
              updateMedia({
                ...video,
                src: data.url,
                id: data.id,
                type: 'stream',
              });
            }
          }}
        >
          {video.name}
          <QualityBadge video={video} />
        </Button>
      ))}
    </m.div>
  );
}

function Skeletons() {
  return (
    <m.div
      key="skeletons"
      className={clsx(className, 'h-40')}
      {...opacityAnimation}
    >
      <Skeleton variant="rect" size="h-full w-[116px]" />
      <Skeleton variant="rect" size="h-full w-[116px]" />
      <Skeleton variant="rect" size="h-full w-[116px]" />
      <Skeleton variant="rect" size="h-full w-[116px]" />
      <Skeleton variant="rect" size="h-full w-[116px]" />
    </m.div>
  );
}
